import { Record, OrderedMap } from 'immutable';
import get from 'lodash/get';
import { Location } from 'models/locations';
import Search, { EVENT_TYPE } from 'models/search';

export default class CuratedLocations extends Record({
  cheapest: null,
  closest: null,
  curated: null,
}) {
  constructor(props, { locale = 'en-us', nonBookableRules } = {}) {
    if (!props) {
      super();
      return;
    }
    const cheapest = CuratedLocations.validLocation(props.cheapest, { locale });
    const closest = CuratedLocations.validLocation(props.closest, { locale });
    const curated = CuratedLocations.validLocation(props.curated, { locale });

    super({
      cheapest,
      closest,
      curated,
    });
  }

  static validLocation(location, { locale, nonBookableRules } = { locale: 'en-us' }) {
    const loc = location && new Location(location, { locale, nonBookableRules });
    if (location && loc.city === 'Chicago' && loc.state === 'IL') {
      return loc;
    }
    return null;
  }

  isValid(locations, currentSearch) {
    const { parkingType, destinationType } = currentSearch;
    const { DAILY_PARKING_TYPE } = Search;
    return !(
      locations.size < 5 ||
      (!this.cheapest && !this.closest && !this.curated) ||
      parkingType !== DAILY_PARKING_TYPE ||
      destinationType === EVENT_TYPE
    );
  }

  forDisplay(locations, currentSearch) {
    if (!this.isValid(locations, currentSearch)) {
      return OrderedMap();
    }

    const results = {};
    if (this.curated) { results['Top Pick'] = this.curated; }
    if (this.cheapest) { results['Best Price'] = this.cheapest; }
    if (this.closest) { results['Closest'] = this.closest; }

    if (this.cheapest && this.curated && this.cheapest.id === this.curated.id) {
      delete results['Best Price'];
    }
    if (this.closest && this.curated && this.closest.id === this.curated.id) {
      delete results.Closest;
    }
    if (results['Best Price'] && results.Closest && this.cheapest && this.closest && this.cheapest.id === this.closest.id) {
      results.Closest = this.cheapest;
      delete results['Best Price'];
    }

    return OrderedMap(results);
  }

  locationLabels(locations, currentSearch) {
    if (!this.isValid(locations, currentSearch)) {
      return OrderedMap();
    }

    const results = {};
    if (this.cheapest) {
      results[this.cheapest.id] = 'Best Price';
    }
    if (this.closest) {
      results[this.closest.id] = 'Closest';
    }
    if (this.curated) {
      results[this.curated.id] = 'Top Pick';
    }

    if (this.cheapest && this.closest && this.cheapest.id === this.closest.id && (!this.curated || this.cheapest.id !== this.curated.id)) {
      results[this.cheapest.id] = 'Closest';
    }

    return OrderedMap(results);
  }

  receiveLocations(curatedLocations) {
    let results = curatedLocations;

    if (this.cheapest && (!results.cheapest || this.cheapest.getQuote().price < results.cheapest.getQuote().price)) {
      results = results.set('cheapest', this.cheapest);
    }

    if (this.curated && (!results.curated || this.curated.getQuote().curationScore < results.curated.getQuote().curationScore)) {
      results = results.set('curated', this.curated);
    }

    if (this.closest && (!results.closest || this.closest.distance < results.closest.distance)) {
      results = results.set('closest', this.closest);
    }

    return results;
  }

  shouldDisplay(locations, currentSearch, brand) {
    return (
      brand.curationEligible &&
      !!this.forDisplay(locations, currentSearch).size &&
      !get(currentSearch, ['destination', 'enhancedAirport'])
    );
  }
}
